/**
 * BadgeUnlockOverlay ✦
 *
 * TikTok-style achievement burst — neon purple / magenta / electric
 * blue. Fires when a freshly unlocked badge arrives in the
 * /api/crew/me/progression payload. Auto-dismisses after ~2.4s
 * unless the user taps to claim. Haptic vibration on supported
 * mobiles. Lightweight: pure CSS keyframes, no extra deps.
 *
 * Optional "Flex it ✦" press inside the overlay opens the
 * FlexShareModal — Instagram-Story-ready 9:16 brag card.
 */
import React, { useEffect, useState } from 'react';
import { Trophy, Star, Sparkle, X as XIcon, Share } from '@phosphor-icons/react';
import FlexShareModal from './FlexShareModal';

const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const FONT_STACK = `'Inter', 'Poppins', system-ui, sans-serif`;

const CONFETTI_COLORS = [PURPLE, PINK, BLUE, '#FFD166', '#FFFFFF'];
const CONFETTI_COUNT = 26;

const Confetti = () => {
  const pieces = Array.from({ length: CONFETTI_COUNT }).map((_, i) => {
    const dx = (Math.random() - 0.5) * 360;
    const dy = -180 - Math.random() * 220;
    const rot = (Math.random() - 0.5) * 720;
    const delay = Math.random() * 0.18;
    const dur = 1.2 + Math.random() * 0.7;
    const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
    const size = 5 + Math.floor(Math.random() * 5);
    return (
      <span
        key={i}
        style={{
          position: 'absolute', left: '50%', top: '50%',
          width: size, height: size * 1.4, background: color,
          borderRadius: 1.5,
          animation: `crewConfetti${i} ${dur}s ease-out ${delay}s forwards`,
          '--dx': `${dx}px`, '--dy': `${dy}px`, '--rot': `${rot}deg`,
        }}
      />
    );
  });
  // Build dynamic keyframes — one per piece for varied trajectories
  const keyframes = Array.from({ length: CONFETTI_COUNT }).map((_, i) => `
    @keyframes crewConfetti${i} {
      0% { transform: translate(-50%, -50%) rotate(0); opacity: 1; }
      100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(var(--rot)); opacity: 0; }
    }
  `).join('\n');
  return (
    <>
      <style>{keyframes}</style>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {pieces}
      </div>
    </>
  );
};

const BadgeUnlockOverlay = ({ badges, onClose, crewName = 'Crew', crewLevel = 1 }) => {
  const [idx, setIdx] = useState(0);
  const [flexOpen, setFlexOpen] = useState(false);

  useEffect(() => {
    if (!badges || badges.length === 0) return;
    if (flexOpen) return; // pause auto-advance while sharing
    // Haptic ping on first frame — purely best-effort.
    try { if (navigator.vibrate) navigator.vibrate([12, 40, 18]); } catch { /* ignore */ }
    const t = setTimeout(() => {
      if (idx < badges.length - 1) setIdx((i) => i + 1);
      else onClose?.();
    }, 3600);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [idx, badges, flexOpen]);

  if (!badges || badges.length === 0) return null;
  const b = badges[idx];

  return (
    <div
      data-testid="badge-unlock-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(7, 7, 26, 0.78)',
        backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'crewOverlayIn 200ms ease-out',
        fontFamily: FONT_STACK,
      }}
    >
      <style>{`
        @keyframes crewOverlayIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes crewBadgePop {
          0%   { transform: scale(0.5); opacity: 0; }
          60%  { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); }
        }
        @keyframes crewBadgeGlow {
          0%, 100% { box-shadow: 0 0 50px ${PINK}66, 0 0 90px ${PURPLE}44; }
          50%      { box-shadow: 0 0 75px ${PINK}AA, 0 0 130px ${PURPLE}88; }
        }
        @keyframes crewRingSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <button
        onClick={onClose}
        data-testid="badge-unlock-close"
        style={{
          position: 'absolute', top: 18, right: 18,
          background: 'rgba(255,255,255,0.08)', border: 'none',
          width: 38, height: 38, borderRadius: '50%',
          color: '#fff', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        aria-label="Close"
      >
        <XIcon size={16} weight="bold" />
      </button>

      <div
        style={{
          position: 'relative',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          textAlign: 'center', maxWidth: 360, padding: '0 24px',
          animation: 'crewBadgePop 460ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        <Confetti />

        <p style={{
          margin: '0 0 8px', fontSize: 11, fontWeight: 800,
          letterSpacing: '0.36em', textTransform: 'uppercase',
          background: `linear-gradient(135deg, ${BLUE}, ${PINK})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          ✦ Badge unlocked
        </p>

        <div
          style={{
            position: 'relative',
            width: 156, height: 156, borderRadius: '50%',
            background: `radial-gradient(circle, ${PURPLE}, ${PINK})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 24,
            animation: 'crewBadgeGlow 1.6s ease-in-out infinite',
          }}
        >
          <div style={{
            position: 'absolute', inset: -6, borderRadius: '50%',
            border: `2px solid ${BLUE}88`,
            borderRightColor: 'transparent', borderTopColor: 'transparent',
            animation: 'crewRingSpin 2.2s linear infinite',
          }} />
          <Trophy size={64} weight="fill" color="#FFFFFF" />
          <Sparkle
            size={20} weight="fill" color="#FFD166"
            style={{ position: 'absolute', top: 10, right: 16 }}
          />
          <Star
            size={16} weight="fill" color="#FFFFFF"
            style={{ position: 'absolute', bottom: 14, left: 18 }}
          />
        </div>

        <h2
          data-testid="badge-unlock-label"
          style={{
            margin: '0 0 6px', fontSize: 28, fontWeight: 900,
            color: '#fff', letterSpacing: '-0.02em',
          }}
        >
          {b.label}
        </h2>
        <p style={{
          margin: '0 0 18px', color: '#B8B8D1', fontSize: 14, lineHeight: 1.55,
        }}>
          {b.blurb}
        </p>
        {badges.length > 1 && (
          <p style={{ margin: 0, fontSize: 11, color: '#B8B8D1', letterSpacing: '0.2em' }}>
            {idx + 1} / {badges.length}
          </p>
        )}

        <button
          onClick={(e) => { e.stopPropagation(); setFlexOpen(true); }}
          data-testid="badge-unlock-flex"
          style={{
            marginTop: 18, padding: '13px 26px', borderRadius: 999,
            border: 'none', cursor: 'pointer', color: '#fff',
            fontFamily: FONT_STACK, fontSize: 14, fontWeight: 900,
            letterSpacing: '0.02em',
            background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
            boxShadow: `0 12px 32px ${PINK}55, 0 0 24px ${PURPLE}55`,
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}
        >
          <Share size={14} weight="fill" /> Flex it ✦
        </button>
      </div>

      <FlexShareModal
        open={flexOpen}
        onClose={() => setFlexOpen(false)}
        kind="badge"
        payload={{
          id: b.id,
          label: b.label,
          blurb: b.blurb,
          name: crewName,
          level: crewLevel,
        }}
      />
    </div>
  );
};

export default BadgeUnlockOverlay;
